import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { deleteImage } from '../../lib/storage'
import EditSubmissionModal from '../EditSubmissionModal'
import AddEventModal from '../AddEventModal'
import EditEventModal from '../EditEventModal'

function AdminDashboard() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('events') // 'events' ou 'submissions'
  const [editingSubmission, setEditingSubmission] = useState(null)
  const [showAddEventModal, setShowAddEventModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)

  useEffect(() => {
    loadEvents()
    loadSubmissions()
  }, [])

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('date_start', { ascending: true })

      if (error) throw error
      setEvents(data || [])
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('event_submissions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      if (error) throw error
      setSubmissions(data || [])
    } catch (error) {
      console.error('Erreur lors du chargement des soumissions:', error)
    }
  }

  const handleDeleteEvent = async (event) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${event.title}" ?`)) {
      return
    }

    try {
      // Supprimer l'image si elle existe
      if (event.image_path) {
        await deleteImage(event.image_path)
      }

      // Supprimer l'événement
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', event.id)

      if (error) throw error

      // Recharger la liste
      loadEvents()
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      alert('Erreur lors de la suppression de l\'événement')
    }
  }

  const handleApproveSubmission = async (submission) => {
    try {
      // Créer l'événement à partir de la soumission
      const { data: newEvent, error: insertError } = await supabase
        .from('events')
        .insert([{
          title: submission.title,
          slug: submission.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          description: submission.description,
          date_start: submission.date_start,
          date_end: submission.date_end,
          date_show: submission.date_show,
          price: submission.price,
          image_url: submission.image_url,
          image_path: submission.image_path,
          alt_text: submission.alt_text,
          categories: submission.categories,
          compte_insta: submission.compte_insta,
          status: 'published',
          created_by: user.id
        }])
        .select()

      if (insertError) throw insertError

      // Marquer la soumission comme approuvée
      const { error: updateError } = await supabase
        .from('event_submissions')
        .update({
          status: 'approved',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', submission.id)

      if (updateError) throw updateError

      // Recharger les listes
      loadEvents()
      loadSubmissions()
      alert('Événement publié avec succès !')
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error)
      alert('Erreur lors de la publication de l\'événement')
    }
  }

  const handleRejectSubmission = async (submission) => {
    const reason = window.prompt('Raison du rejet (optionnel):')

    try {
      const { error } = await supabase
        .from('event_submissions')
        .update({
          status: 'rejected',
          rejection_reason: reason,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', submission.id)

      if (error) throw error

      // L'image sera supprimée automatiquement par le trigger
      loadSubmissions()
      alert('Soumission rejetée')
    } catch (error) {
      console.error('Erreur lors du rejet:', error)
      alert('Erreur lors du rejet de la soumission')
    }
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  const pendingCount = submissions.filter(s => s.status === 'pending').length

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">Dashboard Admin</h1>
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors duration-200"
              >
                ← Retour au site
              </a>
              <span className="text-gray-600">{user?.email}</span>
              <button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition-colors duration-200"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('events')}
              className={`${
                activeTab === 'events'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Événements publiés ({events.length})
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`${
                activeTab === 'submissions'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm relative`}
            >
              Soumissions en attente ({pendingCount})
              {pendingCount > 0 && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {activeTab === 'events' ? (
          <div>
            <div className="mb-4">
              <button
                onClick={() => setShowAddEventModal(true)}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              >
                + Ajouter un événement
              </button>
            </div>

            {/* Liste des événements */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {events.length === 0 ? (
                  <li className="px-6 py-4 text-gray-500 text-center">
                    Aucun événement publié
                  </li>
                ) : (
                  events.map((event) => (
                    <li key={event.id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex gap-4">
                        {event.image_url && (
                          <img
                            src={event.image_url}
                            alt={event.alt_text || event.title}
                            className="w-32 h-32 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
                          <p className="text-sm text-gray-500">{event.date_show}</p>
                          <div className="mt-2 flex gap-2">
                            {Array.isArray(event.categories) && event.categories.map((cat, idx) => (
                              <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {cat}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => setEditingEvent(event)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        ) : (
          <div>
            {/* Liste des soumissions */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {submissions.length === 0 ? (
                  <li className="px-6 py-4 text-gray-500 text-center">
                    Aucune soumission en attente
                  </li>
                ) : (
                  submissions.map((submission) => (
                    <li key={submission.id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex gap-4">
                        {submission.image_url && (
                          <img
                            src={submission.image_url}
                            alt={submission.alt_text || submission.title}
                            className="w-32 h-32 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900">{submission.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">{submission.description}</p>
                          <p className="text-sm text-gray-500 mt-1">{submission.date_show}</p>
                          <p className="text-sm text-gray-500">Contact: {submission.contact_name} ({submission.contact_email})</p>
                          {submission.contact_phone && (
                            <p className="text-sm text-gray-500">Tél: {submission.contact_phone}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => setEditingSubmission(submission)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleApproveSubmission(submission)}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                          >
                            Approuver
                          </button>
                          <button
                            onClick={() => handleRejectSubmission(submission)}
                            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                          >
                            Rejeter
                          </button>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Modaux */}
      {editingSubmission && (
        <EditSubmissionModal
          submission={editingSubmission}
          onClose={() => setEditingSubmission(null)}
          onSave={() => {
            loadSubmissions()
            setEditingSubmission(null)
          }}
        />
      )}

      {showAddEventModal && (
        <AddEventModal
          onClose={() => setShowAddEventModal(false)}
          onSave={() => {
            loadEvents()
            setShowAddEventModal(false)
          }}
        />
      )}

      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSave={() => {
            loadEvents()
            setEditingEvent(null)
          }}
        />
      )}
    </div>
  )
}

export default AdminDashboard
